/**
 * Bank Account domain types.
 * Pure TypeScript - no React imports.
 */

/**
 * Bank account provider (payment processor).
 */
export type BankAccountProvider = "paystack" | "flutterwave" | "stripe";

/**
 * Bank account status.
 */
export type BankAccountStatus = "Active" | "Pending" | "Inactive";

/**
 * Core bank account properties.
 * Used by BankAccount entity and mappers.
 */
export interface BankAccountProps {
  id: number;
  userId: number;
  countryName: string;
  countryCode: string;
  currency: string;
  bankName: string;
  bankCode: string;
  accountNumber: string;
  accountName: string;
  statusId: string;
  status: BankAccountStatus;
  branchCode: string | null;
  provider: BankAccountProvider;
  createdAt: string;
  updatedAt: string;
  loginUrl?: string | null;
}

/**
 * Result from bank account validation.
 */
export interface BankValidationResult {
  accountName: string;
  isValid: boolean;
}

/**
 * A country available for payouts.
 */
export interface PayoutCountry {
  name: string;
  code: string;
  currency: string;
}

/**
 * Country codes where banks have branches that must be selected during payout setup.
 */
export const BANK_BRANCH_COUNTRIES = ["SL", "TD", "CG", "GA", "BJ", "CI", "SN"];

/**
 * A bank available in a country for payouts.
 */
export interface PayoutBank {
  id: number;
  code: string;
  name: string;
  hasBankBranch: boolean;
}

/**
 * A branch of a bank.
 */
export interface BankBranch {
  id: number;
  branchCode: string;
  branchName: string;
  swiftCode: string | null;
  bic: string | null;
  bankId: number;
}

/**
 * Parameters for validating a bank account.
 */
export interface ValidateBankParams {
  accountNumber?: string;
  bankCode: string;
  bankName?: string;
  countryCode: string;
}

/**
 * Parameters for connecting a new bank account.
 */
export interface ConnectBankParams {
  accountNumber: string;
  bankCode: string;
  bankName: string;
  countryCode: string;
  branchCode?: string;
  routingNumber?: string;
}

/**
 * Parameters for updating an existing bank account.
 */
export interface UpdateBankParams extends ConnectBankParams {
  otp: string;
}
